import { ReactNode, useMemo } from 'react';
import { TaskDto, useGetOneTicket, useSubtaskList } from '@sib/api';
import { DEFAULT_DATE_FORMAT, NO_DATA_PLACEHOLDER } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { usePriorityOptions } from '@sib/ui/hooks';
import {
  getTicketNumberLabel,
  minutesToDuration,
  taskCategoryColors,
  ticketPriorityIconColors,
  useTaskStatusLabel,
  taskStatusColors,
} from '@sib/utils';
import { useTaskCategoryOptions } from '@sidecar/hooks/use-task-category-options';
import { format } from 'date-fns';

const TASK_STATUS_COLORS = {
  new: 'hsl(var(--success))',
  completed: 'hsl(var(--muted-foreground))',
} as const;

export const useTaskDetailsTabContent = (task: TaskDto) => {
  const { t } = useTranslation();
  const priorityOptions = usePriorityOptions();
  const categoryOptions = useTaskCategoryOptions();
  const statusLabel = useTaskStatusLabel();
  const { data: subtasks } = useSubtaskList(task.uuid);
  const { data: relatedTicket } = useGetOneTicket(task.ticketId ?? '');
  const relatedTicketNumber = relatedTicket
    ? getTicketNumberLabel(relatedTicket)
    : null;

  const category = useMemo(() => {
    if (!task.category)
      return { label: NO_DATA_PLACEHOLDER, color: '', hasValue: false };

    return {
      label:
        categoryOptions.find(option => option.value === task.category)?.label ??
        task.category,
      color: taskCategoryColors[task.category] ?? '',
      hasValue: true,
    };
  }, [task.category, categoryOptions]);

  const priorityIcon = useMemo((): ReactNode => {
    if (!task.priorityLevel) return null;

    return (
      priorityOptions.find(option => option.value === task.priorityLevel)
        ?.icon ?? null
    );
  }, [task.priorityLevel, priorityOptions]);

  const status = useMemo(
    () => ({
      text: statusLabel[task.status] ?? task.status,
      color: taskStatusColors[task.status] ?? TASK_STATUS_COLORS.new,
    }),
    [statusLabel, task.status]
  );

  const completedSubtasks = useMemo(() => {
    const total = subtasks?.totalItems ?? 0;
    const unfinished = task.unfinishedSubtasksCount ?? 0;
    const completed = Math.max(0, total - unfinished);

    return `${completed}/${total}`;
  }, [subtasks?.totalItems, task.unfinishedSubtasksCount]);

  return {
    title: task.title || NO_DATA_PLACEHOLDER,
    description: task.description || NO_DATA_PLACEHOLDER,
    organizationName: task.organization?.name || NO_DATA_PLACEHOLDER,
    status,
    priority: {
      icon: priorityIcon,
      ...(task.priorityLevel && {
        color: ticketPriorityIconColors[task.priorityLevel],
      }),
      text: task.priorityLevel
        ? t(`enums.Priority.${task.priorityLevel}`)
        : NO_DATA_PLACEHOLDER,
      hasValue: !!task.priorityLevel,
    },
    category,
    assigneeUser: task.assigneeUser,
    createdByUser: task.createdByUser,
    estimatedTime: task.estimatedTime
      ? minutesToDuration(task.estimatedTime)
      : NO_DATA_PLACEHOLDER,
    vendorName: task.vendor?.name || NO_DATA_PLACEHOLDER,
    dueDate: task.deadline
      ? format(new Date(task.deadline), DEFAULT_DATE_FORMAT)
      : NO_DATA_PLACEHOLDER,
    lastUpdated: task.updatedAt
      ? format(new Date(task.updatedAt), DEFAULT_DATE_FORMAT)
      : NO_DATA_PLACEHOLDER,
    completedSubtasks,
    relatedTicketNumber,
    relatedTicketId: task.ticketId ?? null,
  };
};
